import React, { useState, useRef, useEffect } from 'react';
import { Mic, Upload, AlertTriangle, Loader2, Play, Pause, HelpCircle } from 'lucide-react';
import VoiceSelector from './VoiceSelector';
import Tooltip from './Tooltip';
import DenoiseCheckbox from './DenoiseCheckbox';

import { logEnhancedGeneration } from '../utils/enhancedAnalytics';
import { collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { checkAndUpdateQuota, getQuotaStatus } from '../utils/quotaManager';

// Force Speaker Identity Checkbox component
const ForceSpeakerIdentityCheckbox = ({ checked, onChange }) => {
  return (
    <div className="flex items-center mt-2 mb-1">
      <label className="flex items-center cursor-pointer group">
        <div className="relative">
          <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            className="sr-only peer"
          />
          <div className="w-4 h-4 border border-[#3A3D3E] rounded bg-[#181A1B] peer-checked:bg-[#178A94] transition-all duration-200"></div>
          <div className="absolute inset-0 flex items-center justify-center text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        </div>
        <label className="ml-1.5 text-[#EAECEF] text-xs font-semibold">
          Force Speaker Identity
        </label>
        <div className="relative group ml-0.5">
          <HelpCircle
            size={14}
            className="text-[#B0B4BB] hover:text-[#EAECEF] transition-colors cursor-help"
          />
          <div className="absolute left-0 bottom-full mb-1 w-[clamp(12rem,25vw,16rem)]
                          p-2 bg-[#2C2F30] text-[#EAECEF] text-[clamp(0.75rem,1.5vw,0.875rem)]
                          rounded-lg shadow-xl border border-[#3A3D3E]
                          opacity-0 group-hover:opacity-100 transition-opacity
                          pointer-events-none z-10">
            When enabled, this option will force the output audio to sound more like the target voice,
            even if it means losing some of the original voice's characteristics.
            Use this for maximum voice similarity when the target voice is the priority.
            <div className="absolute left-4 bottom-[-4px] w-2 h-2 bg-[#2C2F30]
                          border-r border-b border-[#3A3D3E] transform rotate-45">
            </div>
          </div>
        </div>
      </label>
    </div>
  );
};

const SimilarityAdjuster = ({ value, onChange }) => {
  // Convert 0-0.99 to percentage for visual representation
  const progressWidth = (value / 0.99) * 100;

  return (
    <div className="space-y-2 w-full">
      <div className="flex items-center gap-2">
        {/* Left side - Label and Help */}
        <div className="flex items-center gap-1 min-w-fit relative">
          <label className="text-[#EAECEF] text-xs sm:text-sm font-semibold whitespace-nowrap">
            Similarity
          </label>
          <div className="relative group hidden sm:block">
            <AlertTriangle
              size={14}
              className="text-[#B0B4BB] hover:text-[#EAECEF] transition-colors cursor-help"
            />
            <div className="absolute left-0 bottom-full mb-1 w-[clamp(12rem,25vw,16rem)]
                          p-2 bg-[#2C2F30] text-[#EAECEF] text-[clamp(0.75rem,1.5vw,0.875rem)]
                          rounded-lg shadow-xl border border-[#3A3D3E]
                          opacity-0 group-hover:opacity-100 transition-opacity
                          pointer-events-none z-10">
              Adjust how similar the output voice sounds to the target voice. Higher values preserve more of the target voice characteristics,
              while lower values retain more of the original audio's unique qualities.
              <div className="absolute left-4 bottom-[-4px] w-2 h-2 bg-[#2C2F30]
                            border-r border-b border-[#3A3D3E] transform rotate-45">
              </div>
            </div>
          </div>
        </div>

        {/* Middle - Slider */}
        <div className="flex-1">
          <input
            type="range"
            min="0"
            max="0.99"
            step="0.01"
            value={value}
            onChange={onChange}
            className="w-full h-[0.2rem] rounded-full appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-[0.75rem]
                    [&::-webkit-slider-thumb]:h-[0.75rem]
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-[#178A94]
                    [&::-webkit-slider-thumb]:hover:bg-[#146E75]
                    [&::-webkit-slider-thumb]:transition-colors"
            style={{
              background: `linear-gradient(to right, #EAECEF ${progressWidth}%, #181A1B ${progressWidth}%)`
            }}
          />
        </div>

        {/* Right side - Value display */}
        <span className="rounded min-w-[3.5ch] text-center font-medium
                      text-sm text-[#EAECEF] bg-[#178A94] px-2 py-0.5">
          {value.toFixed(2)}
        </span>
      </div>

      {/* Bottom labels */}
      <div className="flex justify-between text-[0.65rem] px-1 items-center">
        <div className="flex items-center">
          <span className="font-bold text-[#EAECEF]">Original</span>
          <span className="text-[#B0B4BB] hidden sm:inline-block ml-1">(Retain your voice)</span>
        </div>
        <div className="flex items-center">
          <span className="font-bold text-[#EAECEF]">Target</span>
          <span className="text-[#B0B4BB] hidden sm:inline-block ml-1">(Match target voice)</span>
        </div>
      </div>
    </div>
  );
};

const PaceAdjuster = ({ value, onChange }) => {
  const progressWidth = ((value - 1) / (4 - 1)) * 100;

  return (
    <div className="space-y-2 w-full">
      <div className="flex items-center gap-2">
        {/* Left side - Label and Help */}
        <div className="flex items-center gap-1 min-w-fit relative">
          <label className="text-[#EAECEF] text-xs sm:text-sm font-semibold whitespace-nowrap">
            Pace
          </label>
          <div className="relative group hidden sm:block">
            <AlertTriangle
              size={14}
              className="text-[#B0B4BB] hover:text-[#EAECEF] transition-colors cursor-help"
            />
            <div className="absolute left-0 bottom-full mb-1 w-[clamp(12rem,25vw,16rem)]
                            p-2 bg-[#2C2F30] text-[#EAECEF] text-[clamp(0.75rem,1.5vw,0.875rem)]
                            rounded-lg shadow-xl border border-[#3A3D3E]
                            opacity-0 group-hover:opacity-100 transition-opacity
                            pointer-events-none z-10">
              Control the speaking speed of the converted audio. Level 1 maintains the original pace,
              while higher values result in faster delivery.
              <div className="absolute left-4 bottom-[-4px] w-2 h-2 bg-[#2C2F30]
                              border-r border-b border-[#3A3D3E] transform rotate-45">
              </div>
            </div>
          </div>
        </div>

        {/* Middle - Slider */}
        <div className="flex-1">
          <input
            type="range"
            min="1"
            max="4"
            step="1"
            value={value}
            onChange={onChange}
            className="w-full h-[0.2rem] rounded-full appearance-none cursor-pointer
                     [&::-webkit-slider-thumb]:appearance-none
                     [&::-webkit-slider-thumb]:w-[0.75rem]
                     [&::-webkit-slider-thumb]:h-[0.75rem]
                     [&::-webkit-slider-thumb]:rounded-full
                     [&::-webkit-slider-thumb]:bg-[#178A94]
                     [&::-webkit-slider-thumb]:hover:bg-[#146E75]
                     [&::-webkit-slider-thumb]:transition-colors"
            style={{
              background: `linear-gradient(to right, #EAECEF ${progressWidth}%, #181A1B ${progressWidth}%)`
            }}
          />
        </div>

        {/* Right side - Value display */}
        <span className="rounded min-w-[2.5ch] text-center font-medium
                      text-sm text-[#EAECEF] bg-[#178A94] px-2 py-0.5">
          {value === 1 ? '1' : value}
        </span>
      </div>

      {/* Bottom labels */}
      <div className="flex justify-between text-[0.65rem] px-1 items-center">
        <div className="flex items-center">
          <span className="font-bold text-[#EAECEF]">Original</span>
          <span className="text-[#B0B4BB] hidden sm:inline-block ml-1">(Same speed)</span>
        </div>
        <div className="flex items-center">
          <span className="font-bold text-[#EAECEF]">Fast</span>
          <span className="text-[#B0B4BB] hidden sm:inline-block ml-1">(Increased speed)</span>
        </div>
      </div>
    </div>
  );
};

// Credits per minute of audio
const CREDITS_PER_MINUTE = 1000;
// Maximum audio duration in seconds
const MAX_AUDIO_DURATION = 90; // 90 seconds limit

// Pulsing record button animation
const PulsingRecordButton = ({ isRecording, onClick, disabled }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="relative flex items-center justify-center"
    >
      {/* Outer animated pulse ring (conditionally rendered) */}
      {isRecording && (
        <span className="absolute w-24 h-24 rounded-full bg-red-500 opacity-20 animate-ping"></span>
      )}
      
      {/* Base circle */}
      <div className={`relative flex items-center justify-center w-20 h-20 rounded-full transition-colors ${isRecording ? 'bg-red-600' : 'bg-[#178A94] hover:bg-[#146E75]'}`}>
        {/* Icon */}
        <Mic 
          size={32} 
          className={`text-white transition-all ${isRecording ? 'animate-pulse' : ''}`}
        />
      </div>
    </button>
  );
};

const VoiceChanger = ({
  user = {},
  voices = [],
  setQuotaStatus = () => {},
  onAudioGenerated = () => {},
  selectedVoice = null,
  setSelectedVoice = null,
  onClose = null
}) => {
  // Filter out the "Akhil" voice (ID 36) from the displayed voices in VoiceChanger
  const filteredVoices = voices.filter(voice => voice.id !== 36);
  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  
  // File upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // Input audio state (either recorded or uploaded)
  const [inputAudio, setInputAudio] = useState(null);
  const [inputAudioDuration, setInputAudioDuration] = useState(0);
  const [inputAudioName, setInputAudioName] = useState('');
  const [inputAudioUrl, setInputAudioUrl] = useState(null);

  // Voice selection and parameters state
  const [voice, setVoice] = useState(() => {
    // Initialize with selectedVoice, unless it's Akhil (36)
    if (selectedVoice === 36 && filteredVoices.length > 0) {
      return filteredVoices[0]?.id || null;
    }
    return selectedVoice || (filteredVoices[0]?.id || voices[0]?.id || null);
  });
  const [similarity, setSimilarity] = useState(0.75);
  const [pace, setPace] = useState(1);  // Added pace state: 1 = Original
  const [isConverting, setIsConverting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [denoiseAudio, setDenoiseAudio] = useState(true);
  const [forceSpeakerIdentity, setForceSpeakerIdentity] = useState(false); // Force speaker identity option

  // Refs for recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null); // Use ref instead of state for immediate access
  const fileInputRef = useRef(null);
  const uploadAreaRef = useRef(null);
  
  // Update local voice when selected voice changes from parent
  useEffect(() => {
    if (selectedVoice) {
      // If selectedVoice is Akhil (ID 36), select another voice instead, but only on first render
      if (selectedVoice === 36 && filteredVoices.length > 0 && voice === null) {
        setVoice(filteredVoices[0].id);
      } else if (selectedVoice !== 36) {
        setVoice(selectedVoice);
      }
    }
  }, [selectedVoice]);

  // Update parent when voice changes locally
  useEffect(() => {
    if (setSelectedVoice && voice !== selectedVoice && voice !== null) {
      // Don't update if the new voice is Akhil (36)
      if (voice !== 36) {
        setSelectedVoice(voice);
      }
    }
  }, [voice, selectedVoice, setSelectedVoice]);

  // Clean up URLs when component unmounts
  useEffect(() => {
    return () => {
      if (inputAudioUrl) {
        URL.revokeObjectURL(inputAudioUrl);
      }
    };
  }, [inputAudioUrl]);

  // Remove audio ended event handling since we'll use GlobalAudioPlayer

  // Handle recording start
  const startRecording = async () => {
    try {
      // If there's an active recording, stop it first
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
        clearInterval(recordingTimerRef.current);

        // Give the recorder time to process the stop event
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Reset recording state completely
      audioChunksRef.current = [];
      setRecordingDuration(0);

      // Close any playing audio if a new recording starts
      if (onClose && inputAudio) {
        onClose();
      }

      // Clear all previous audio data
      setInputAudio(null);
      setInputAudioName('');
      if (inputAudioUrl) {
        URL.revokeObjectURL(inputAudioUrl);
      }
      setInputAudioUrl(null);
      setUploadedFile(null);

      // Setup MediaRecorder with 16kHz audio
      const options = {
        mimeType: 'audio/webm'
      };

      mediaRecorderRef.current = new MediaRecorder(stream, options);

      mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      });

      // Store the Firebase timestamp when recording starts directly in the ref for immediate access
      recordingStartTimeRef.current = Timestamp.now();
      
      mediaRecorderRef.current.addEventListener('stop', () => {
        // Convert WebM to MP3 for better browser support
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

        // Create a File from the Blob to better handle the data
        const currentDate = new Date();
        const fileName = `recording-${currentDate.toISOString()}.webm`;
        const audioFile = new File([audioBlob], fileName, { type: 'audio/webm' });

        // Create URL for playback
        const url = URL.createObjectURL(audioBlob);

        // Calculate accurate duration using Firebase timestamps via the ref
        const endTime = Timestamp.now();
        let accurateDuration = 0;

        if (recordingStartTimeRef.current) {
          // Get seconds difference between timestamps
          accurateDuration = endTime.seconds - recordingStartTimeRef.current.seconds;

          // Account for nanoseconds difference
          const nanoSecDiff = endTime.nanoseconds - recordingStartTimeRef.current.nanoseconds;
          const nanoSecAdjustment = nanoSecDiff / 1000000000; // Convert to seconds

          accurateDuration += nanoSecAdjustment;

          // Duration calculated accurately from timestamps
        } else {
          // No recording start time available, falling back to JS timer
        }

        // Ensure duration is reasonable - never less than 1 second
        const finalDuration = (accurateDuration > 0) ? Math.max(Math.ceil(accurateDuration), 1) :
                             (recordingDuration > 0 ? recordingDuration : 1);

        // Reset the timestamp ref for next recording
        recordingStartTimeRef.current = null;

        // Set the input audio data for recorded audio
        setInputAudio(audioFile);
        setInputAudioDuration(finalDuration);
        setInputAudioName('Recorded Audio');
        setInputAudioUrl(url);

        // Stop all tracks from the stream
        stream.getTracks().forEach(track => track.stop());
      });
      
      // Start recording
      mediaRecorderRef.current.start();
      setIsRecording(true);
      
      // Start duration timer
      let startTime = Date.now();
      recordingTimerRef.current = setInterval(() => {
        const currentDuration = Math.floor((Date.now() - startTime) / 1000);
        setRecordingDuration(currentDuration);
        
        // Stop recording if it exceeds the maximum duration
        if (currentDuration >= MAX_AUDIO_DURATION) {
          stopRecording();
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Could not access microphone. Please allow microphone access or try a different browser.');
    }
  };

  // Handle recording stop
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null; // Clear the timer reference
    }
  };
  
  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files[0]);
    }
  };
  
  // Handle file input change
  const handleFileUpload = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files[0]);
    }
  };
  
  // Process the uploaded file
  const handleFiles = (file) => {
    if (!file) return;

    // Check if the file is a valid audio type (WAV or MP3)
    if (!file.type.match('audio/wav') && !file.type.match('audio/mpeg')) {
      alert('Please upload a WAV or MP3 file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Close any playing audio if a new file is uploaded
    if (onClose && inputAudio) {
      onClose();
    }

    // Create URL for the uploaded file
    const fileUrl = URL.createObjectURL(file);

    // Get file duration
    const audio = new Audio();
    audio.src = fileUrl;

    audio.addEventListener('loadedmetadata', () => {
      const duration = Math.floor(audio.duration);

      // Check if audio exceeds maximum duration
      if (duration > MAX_AUDIO_DURATION) {
        alert(`Audio exceeds the maximum duration of ${MAX_AUDIO_DURATION} seconds. Please upload a shorter audio file (less than 90 seconds).`);
        URL.revokeObjectURL(fileUrl);
        setUploadedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      setInputAudioDuration(duration);
      setUploadedFile(file);
      setInputAudio(file);
      setInputAudioName(file.name);
      setInputAudioUrl(fileUrl);

      // Don't automatically send to GlobalAudioPlayer - wait for play button click
    });
    
    audio.addEventListener('error', () => {
      alert('Failed to load audio file. Please try a different file.');
      URL.revokeObjectURL(fileUrl);
      setUploadedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    });
  };
  
  // Play audio using GlobalAudioPlayer
  const handlePlayAudio = () => {
    if (!inputAudioUrl) return;

    const audioTitle = uploadedFile ? uploadedFile.name : 'Recorded Audio';
    const previewAudio = {
      url: inputAudioUrl,
      title: `Input: ${audioTitle}`,
      type: 'input-audio',
      id: 'input-' + Date.now()
    };
    onAudioGenerated(previewAudio);
  };

  // Calculate credits required for conversion
  const calculateCreditsRequired = (durationSecs) => {
    if (!durationSecs || isNaN(durationSecs) || durationSecs <= 0) {
      return 0;
    }
    const durationMins = durationSecs / 60;
    // Apply 1.25x multiplier if denoising is enabled (25% extra instead of 50%)
    const denoiseMultiplier = denoiseAudio ? 1.25 : 1;
    return Math.ceil(durationMins * CREDITS_PER_MINUTE * denoiseMultiplier);
  };

  // Handle audio conversion
  const handleConvert = async () => {
    if (!inputAudio || !voice || isConverting) return;

    // Calculate credits with proper denoising multiplier (1.25x if denoising is enabled)
    const creditsRequired = calculateCreditsRequired(inputAudioDuration);

    // Check if user has enough credits
    try {
      // Check if user object is properly defined
      if (!user || !user.uid) {
        alert('User information is missing. Please log in again.');
        return;
      }

      const quotaCheck = await checkAndUpdateQuota(user.uid, creditsRequired);
      if (!quotaCheck.canProceed) {
        alert(`You don't have enough credits. Required: ${creditsRequired.toLocaleString()}, Available: ${quotaCheck.remainingChars.toLocaleString()}`);
        return;
      }

      setIsConverting(true);

      try {
        // Step 1: Get a blob URL from Azure function - using the same function as the denoiser
        const blobResponse = await fetch(
          "https://voiceops.azurewebsites.net/api/createblobobject?code=TG1_oEhJKuhbzFkJuRnUllLE8L6UdkzFBnU6Vp535PSpAzFuVq_xpA==",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              userId: user.uid,
              format: uploadedFile ? uploadedFile.type.split('/')[1] : 'webm'
            })
          }
        );

        if (!blobResponse.ok) {
          throw new Error(`Failed to get blob upload URL: ${blobResponse.status} ${blobResponse.statusText}`);
        }

        const blobData = await blobResponse.json();
        const { blobUrl, blobName } = blobData;

        if (!blobUrl || !blobName) {
          throw new Error('Invalid blob upload URL received');
        }

        // Step 2: Upload audio file to the blob URL
        const uploadResponse = await fetch(blobUrl, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': uploadedFile ? uploadedFile.type : 'audio/webm'
          },
          body: inputAudio
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload audio to blob: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        // Once uploaded successfully, proceed to voice conversion
        const inputBlobUrl = `https://luciddenoised.blob.core.windows.net/input/${blobName}`;

        // Step 3: Call voice conversion Azure function with the blob URL
        const voiceChangerResponse = await fetch(
          "https://voicechanger.azurewebsites.net/api/helper?code=kQ-UIrNfrl7h5j4YLVqHHD8gTdwszivNlrcsCnqgUh_RAzFu5psyow==",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              audio_url: inputBlobUrl,
              voice_id: voice,
              pace: pace,
              cfg: similarity,  // cfg is the similarity parameter
              denoise: denoiseAudio,
              force_speaker_identity: forceSpeakerIdentity
            })
          }
        );

        if (!voiceChangerResponse.ok) {
          throw new Error(`Voice conversion failed: ${voiceChangerResponse.status} ${voiceChangerResponse.statusText}`);
        }

        const voiceChangerData = await voiceChangerResponse.json();

        if (!voiceChangerData.dl_url) {
          throw new Error('No converted audio URL returned');
        }

        // Create audio object with metadata for the player
        const audioData = {
          url: voiceChangerData.dl_url,
          title: `Voice Changed: ${inputAudioName}`,
          type: 'voice-changed',
          id: 'voice-changed-' + Date.now()
        };

        // Pass the generated audio back to parent
        onAudioGenerated(audioData);

        // Log the successful generation to both existing logs and new voice_changer_logs
        if (user && user.uid && user.email) {
          // Log to regular generation logs for backward compatibility
          await logEnhancedGeneration(
            user.uid,
            user.email,
            voice,
            creditsRequired, // Using credits instead of character count
            `Voice Changer${denoiseAudio ? ' (Denoised)' : ''}${forceSpeakerIdentity ? ' (Forced Identity)' : ''}`, // Context
            voiceChangerData.dl_url,
            similarity,
            "success", // success string instead of boolean
            pace // Add pace parameter
          );

          // Log to dedicated voice changer logs with more detailed information
          await addDoc(collection(db, 'voice_changer_logs'), {
            userId: user.uid,
            userEmail: user.email,
            voiceId: voice,
            inputDuration: inputAudioDuration,
            inputType: uploadedFile ? 'uploaded' : 'recorded',
            inputFormat: uploadedFile ? uploadedFile.type : 'audio/webm',
            similarity: similarity,
            pace: pace,
            denoise: denoiseAudio,
            forceSpeakerIdentity: forceSpeakerIdentity,
            creditsUsed: creditsRequired,
            outputUrl: voiceChangerData.dl_url,
            inputBlobUrl: `https://luciddenoised.blob.core.windows.net/input/${blobName}`,
            timestamp: serverTimestamp(),
            success: "success",
            blobName: blobName,
            requestDetails: {
              audioSource: uploadedFile ? uploadedFile.name : 'Recording',
              audioDuration: inputAudioDuration,
              audioSize: inputAudio.size || 0,
              requestTimestamp: serverTimestamp(),
              processingTimeStart: Date.now(),
              conversionSettings: {
                similarity: similarity,
                pace: pace,
                denoise: denoiseAudio,
                forceSpeakerIdentity: forceSpeakerIdentity
              }
            }
          });

          // Update quota display after successful conversion
          if (setQuotaStatus) {
            const newStatus = await getQuotaStatus(user.uid);
            setQuotaStatus(newStatus);
          }
        }
        
      } catch (error) {
        console.error('Conversion failed:', error);
        if (user && user.uid && user.email) {
          // Log to regular generation logs
          await logEnhancedGeneration(
            user.uid,
            user.email,
            voice,
            creditsRequired,
            `Voice Changer${denoiseAudio ? ' (Denoised)' : ''}${forceSpeakerIdentity ? ' (Forced Identity)' : ''}`,
            null, // no output URL for failed attempts
            similarity,
            "failed", // failed string instead of boolean
            pace // Add pace parameter
          );

          // Log to dedicated voice changer logs
          await addDoc(collection(db, 'voice_changer_logs'), {
            userId: user.uid,
            userEmail: user.email,
            voiceId: voice,
            inputDuration: inputAudioDuration,
            inputType: uploadedFile ? 'uploaded' : 'recorded',
            inputFormat: uploadedFile ? uploadedFile.type : 'audio/webm',
            similarity: similarity,
            pace: pace,
            denoise: denoiseAudio,
            forceSpeakerIdentity: forceSpeakerIdentity,
            creditsUsed: creditsRequired,
            error: error.message || 'Unknown error',
            inputBlobUrl: null,
            timestamp: serverTimestamp(),
            success: "failed",
            requestDetails: {
              audioSource: uploadedFile ? uploadedFile.name : 'Recording',
              audioDuration: inputAudioDuration,
              audioSize: inputAudio ? inputAudio.size : 0,
              errorDetails: error.toString(),
              processingTimeStart: Date.now(),
              processingFailed: true,
              conversionSettings: {
                similarity: similarity,
                pace: pace,
                denoise: denoiseAudio,
                forceSpeakerIdentity: forceSpeakerIdentity
              }
            }
          });
        }
        alert('Failed to convert audio. Please try again.');
      } finally {
        setIsConverting(false);
      }
    } catch (error) {
      console.error('Error checking quota:', error);
      alert('Failed to check quota. Please try again.');
    }
  };
  
  // Format time for display
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) {
      return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Determine if we have a recorded or uploaded audio
  const isRecordedAudio = inputAudio && !uploadedFile;
  const isUploadedAudio = inputAudio && uploadedFile;

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="grid grid-cols-12 gap-[1rem]">
        {/* Left Column - Audio Input & Controls */}
        <div className="col-span-12 lg:col-span-8 bg-[#2C2F30] rounded-xl shadow-lg border border-[#3A3D3E] p-[1.25rem] h-[clamp(500px,75vh,900px)] flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-bold text-[#EAECEF]">Voice Changer</div>

            {/* Tooltip on the Far Right */}
            <div className="flex items-center">
              <Tooltip
                text={[
                  "Upload or record audio up to 90 seconds long.",
                  "Only WAV and MP3 files are supported for upload.",
                  "Higher similarity values will sound more like the target voice.",
                  "1000 credits are required per minute of audio conversion."
                ]}
              />
            </div>
          </div>

          {/* Main working area - 80% of height */}
          <div className="flex-grow relative bg-[#181A1B] rounded-xl overflow-hidden mb-5 border border-[#3A3D3E]">
            {/* Vertical divider */}
            <div className="absolute left-1/2 top-4 bottom-4 border-l border-dashed border-[#178A94] opacity-60 transform -translate-x-1/2"></div>
            
            {/* Left side - Recording */}
            <div className="absolute left-0 top-0 w-1/2 h-full flex items-center justify-center">
              <div className="flex flex-col items-center">
                <PulsingRecordButton 
                  isRecording={isRecording} 
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isConverting}
                />
                <div className="mt-4 text-[#B0B4BB]">
                  {isRecording ? `Recording... ${formatTime(recordingDuration)}` : 'Tap to record'}
                </div>
              </div>
            </div>
            
            {/* Right side - File Upload */}
            <div className="absolute right-0 top-0 w-1/2 h-full">
              <div 
                ref={uploadAreaRef}
                className={`h-full flex flex-col items-center justify-center p-6 transition-colors
                          ${dragActive ? 'bg-[#178A94]/20' : 'hover:bg-[#2C2F30]/30'}
                          ${isConverting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => !isConverting && !isRecording && fileInputRef.current.click()}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className={`p-4 rounded-full mb-3 transition-colors
                               ${dragActive ? 'bg-[#178A94]/40' : 'bg-[#2C2F30]'}`}>
                  <Upload size={28} className="text-[#178A94]" />
                </div>
                <div className="text-center">
                  <p className="text-[#EAECEF] mb-1">Drop audio file here</p>
                  <p className="text-[#B0B4BB] text-sm">or click to browse</p>
                  <p className="text-[#7C8085] text-xs mt-3">Supported: .wav, .mp3</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="audio/wav,audio/mpeg"
                  onChange={handleFileUpload}
                  disabled={isConverting || isRecording}
                />
              </div>
            </div>
          </div>
          
          {/* Audio Status - 15% of height */}
          {inputAudio && (
            <div className="bg-[#2C2F30] rounded-lg border border-[#3A3D3E] p-3 mb-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1 min-w-0 mr-2">
                  <div className="mr-2 flex-shrink-0 py-1 px-2 bg-[#178A94]/20 rounded text-[#178A94] text-xs font-medium uppercase">
                    Ready
                  </div>
                  <div className="text-[#EAECEF] font-medium truncate min-w-0 flex-1">
                    {inputAudioName}
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* Show duration for all audio types */}
                  {inputAudioDuration > 0 && (
                    <div className="text-[#B0B4BB] text-sm">
                      {formatTime(inputAudioDuration)}
                    </div>
                  )}

                  {/* Play button for all audio types */}
                  <button
                    onClick={handlePlayAudio}
                    className="p-2 bg-[#178A94] rounded-full hover:bg-[#146E75] transition-colors"
                  >
                    <Play size={14} className="text-white" />
                  </button>
                </div>
              </div>

              {/* Show credits estimate for all audio with known duration */}
              {inputAudioDuration > 0 && (
                <div className="mt-2 text-xs text-center text-[#B0B4BB]">
                  Est. credits: {calculateCreditsRequired(inputAudioDuration).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {/* Parameter controls: Similarity, Pace, and checkboxes */}
          <div className="space-y-3">
            {/* Sliders side by side in a grid */}
            <div className="grid grid-cols-2 gap-2">
              <div className="pr-1">
                <SimilarityAdjuster
                  value={similarity}
                  onChange={(e) => setSimilarity(parseFloat(e.target.value))}
                />
              </div>
              <div className="pl-1">
                <PaceAdjuster
                  value={pace}
                  onChange={(e) => setPace(parseInt(e.target.value))}
                />
              </div>
            </div>
            <DenoiseCheckbox
              checked={denoiseAudio}
              onChange={(e) => setDenoiseAudio(e.target.checked)}
            />
            <ForceSpeakerIdentityCheckbox
              checked={forceSpeakerIdentity}
              onChange={(e) => setForceSpeakerIdentity(e.target.checked)}
            />
          </div>
        </div>

        {/* Right Column - Voice Selector */}
        <div className="col-span-12 lg:col-span-4 bg-[#2C2F30] rounded-xl shadow-lg border border-[#3A3D3E] p-[1.25rem] h-[clamp(500px,75vh,900px)]
                    overflow-y-auto
                    scrollbar scrollbar-w-[0.5rem]
                    scrollbar-thumb-[#178A94] hover:scrollbar-thumb-[#146E75]
                    scrollbar-track-[#2C2F30]
                    scrollbar-thumb-rounded-full
                    scrollbar-track-rounded-full">
          <VoiceSelector
            voices={filteredVoices}
            selectedVoice={voice}
            onVoiceSelect={setVoice}
            onPreviewPlay={(voiceId, previewUrl, voiceName) => {
              // Create preview audio object
              const previewAudio = {
                url: previewUrl,
                title: `Preview: ${voiceName}`,
                type: 'preview',
                id: voiceId
              };
              onAudioGenerated(previewAudio);
            }}
            currentlyPlaying={null} // This will be managed by GlobalAudioPlayer
          />
        </div>
      </div>
    
      {/* Convert Button */}
      <div className="w-full">
        <button
          onClick={handleConvert}
          disabled={!inputAudio || !voice || isConverting}
          className="w-full p-[1.15rem] bg-[#178A94] text-[#EAECEF] rounded-lg uppercase font-bold
                  hover:bg-[#146E75] transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center text-[clamp(1rem,1.5vw,1.125rem)]
                  mb-[4rem] sm:mb-[2rem]"
        >
          {isConverting ? (
            <>
              <Loader2 className="animate-spin mr-[0.5rem]" size={24} /> 
              Converting...
            </>
          ) : (
            <>
              <span className="mr-[0.5rem] text-xl">⚡</span>
              Convert Voice
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default VoiceChanger;